'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import HeaderImage from '@/app/assets/imgs/char2v/header.png';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReferenceVideo } from '@/app/components/icons/BaseLineReferenceVideo';
import { BaseLineReplace } from '@/app/components/icons/BaseLineReplace';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useFlyTarget } from '@/app/components/shared/FlyOverlay/use-fly-target';
import { FormSubmitButton } from '@/app/components/shared/FormSubmitButton';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { MentionsTextarea } from '@/app/components/shared/MentionsTextarea';
import { UpgradeLabel } from '@/app/components/shared/UpgradeLabel';
import { type VoiceSelectionCustomOption, VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import {
  isEnterpriseDistribution,
  MAX_AUDIO_REFERENCE_PROMPTS,
  MODEL_VERSION,
  Q4_MANUAL_URL,
  Q4_REFERENCE_AUDIO_ACCEPT,
  Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
  Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
  Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
  Q4_REFERENCE_IMAGE_MAX_FILE_SIZE,
  SITE,
  TTS_VOICE_MAP,
  type TTSVoiceOption,
} from '@/app/constants';
import {
  isReferenceAudioDurationValid,
  resolveReferenceAudioDuration,
  useAudioUpload,
  useDevice,
  useFetchRecreateData,
  useForm,
  useGlobalModal,
  useMentionablePrompts,
  useModelTrialCases,
  useReferenceTempAudiosUpload,
  useReferenceTempVideosUpload,
  useResizeByResolution,
  useRunTempReferenceImageUploadBatch,
  useUpgradeDialogs,
  validateReferenceAudioDurations,
} from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  AudioUploadFile,
  ElementInfo,
  ImageUploadFile,
  ReferencePrompt,
  SourceAudio,
  UploadFile,
  UploadImageType,
  VideoUploadFile,
} from '@/app/types';
import {
  appendUnMentionedReferences,
  cn,
  getAudioReferenceCount,
  getImageAndElementCount,
  getImageAndImageElementCount,
  getMaxImageAndElementCount,
  getMentionColor,
  getNextAudioName,
  getNextAvailableAudioIdx,
  getNextImageName,
  getPromptMaxLength,
  getReferenceElementModalities,
  getReferenceMediaDropArgs,
  getReferenceVideoElementMaxCount,
  getTextElementCount,
  getVideoAndVideoElementCount,
  isReferenceVideoSupported,
  reportEvent,
  validateAndCorrectSettings,
} from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { useLocale } from '@/i18n/routing';
import { ElementsDialog } from '../../_components/ElementsDialog';
import { ExpandableTrySamplesButton } from '../../_components/ExpandableTrySamplesButton';
import { FormTextarea } from '../../_components/FormTextarea';
import { PromptLibraryPopover } from '../../_components/PromptLibraryPopover';
import { ReferencePromptsGrid } from '../../_components/ReferencePromptsGrid';
import { Settings } from '../../_components/Settings';
import { TryImageSamples } from '../../_components/TryImageSamples';
import { UserGuide } from '../../_components/UserGuide';
import { VideoImageReferencesGrid } from '../../_components/VideoImageReferencesGrid';
import { useBackfillData } from '../../_hooks/use-backfill-data';
import { useCheckMaterialElements } from '../../_hooks/use-check-material-elements';
import { useElementsManager } from '../../_hooks/use-reference-management';
import { useCreationConsent } from '../../(tools)/_hooks/use-creation-consent';
import { getChar2VTutorials } from '../../constants';
import { useLayoutManager } from './_hooks/use-layout-manager';
import { useVideoState } from './_hooks/use-video-state';

const CHAR2VIDEO_TUTORIAL_VIDEO_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/assets/char2video-new-tutorial.62de0528.mp4'
    : 'https://image01.cf.vidu.studio/vidu/assets/char2video-new-tutorial.62de0528.mp4';

const getElementVoiceAudioId = (elementId: string) => `element-voice-audio-${elementId}`;

const getElementVoiceAudio = (element: ElementInfo): SourceAudio | null => {
  const voiceId = element.voice?.id?.trim();
  const voiceUrl = element.voice?.url?.trim() || (voiceId ? TTS_VOICE_MAP[voiceId]?.url : undefined);

  if (!element.id || !voiceId || !voiceUrl) return null;

  return {
    id: voiceId,
    name: element.voice?.name || TTS_VOICE_MAP[voiceId]?.name || element.name,
    src: voiceUrl,
    preview: voiceUrl,
    originElementId: element.id,
    originElementVoiceId: voiceId,
    originElementVoiceIsMy: element.voice?.is_my,
    isElementVoiceReference: true,
  };
};

const isElementVoiceAudioPrompt = (prompt: ReferencePrompt['prompt']): prompt is SourceAudio => {
  return !!prompt && 'isElementVoiceReference' in prompt && !!prompt.isElementVoiceReference;
};

const Char2VideoForm: FC = () => {
  const t = useTranslations();
  const locale = useLocale();
  const storeApi = useStoreApi();

  const isInspireMe = useRef<boolean>(false);
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState<boolean | string>(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const {
    isProMode,
    isProModeWarningTipVisible,
    setChar2VideoWarningTipVisible,
    setFormParams,
    modelVersion,
    userInfo,
  } = useStore((state) => ({
    isProMode: state.character2videoFormParams.isProMode,
    isProModeWarningTipVisible: state.character2videoFormParams.isProModeWarningTipVisible,
    setChar2VideoWarningTipVisible: state.setChar2VideoWarningTipVisible,
    setFormParams: state.setFormParams,
    modelVersion: state.character2videoFormParams.modelVersion,
    userInfo: state.userInfo,
  }));
  const flyEndTargetRef = useFlyTarget();

  useFetchRecreateData();

  const { handleOpenPlanDialog } = useUpgradeDialogs();
  const [openPromptLibraryPopover, setOpenPromptLibraryPopover] = useState(false);
  const supportVideoReference = isReferenceVideoSupported(modelVersion);
  const isQ4Reference = modelVersion === MODEL_VERSION.Q4;
  const maxImageAndElementCount = getMaxImageAndElementCount(modelVersion);

  const showReferenceAudioDurationError = useCallback(
    (duration?: number) => {
      toast({
        type: 'error',
        message:
          duration === undefined
            ? t('sharedComponent.audioUploader.error.durationUnavailable')
            : t('sharedComponent.audioUploader.error.supportedDurationRange', {
                min: Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
                max: Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
              }),
      });
    },
    [t],
  );

  const allowedModalities = useMemo(() => {
    const modalities = getReferenceElementModalities(modelVersion);
    return modalities ? [...modalities] : undefined;
  }, [modelVersion]);

  const {
    settings,
    settingsConfig,
    handleCheckSettings,
    updateSettings,
    handleBeforeProModeSubmit,
    canUseProMode,
    formValidationErrorMessage,
    shouldUpgradeForModel,
    handleUpgradeClick,
  } = useForm('character2video', (fromPage?: string) => handleOpenPlanDialog(fromPage ?? 'prompt_pro_mode'));

  const {
    textPrompt,
    updateTextPrompt,
    referencePrompts,
    addReferenceAndMentions,
    removeReferenceAndMentions,
    updateReferenceAndMentions,
    removeReferenceFromTrash,
    handleUploadImage,
    handleClearPrompts,
    setPromptFromExample,
    handleBeforeAddMention,
    showMaxReferenceError,
    resetMaxReferenceError,
    handleReplaceImage,
    handleUploadVideo,
    handleUploadAudio,
    handleSwapReferences,
  } = useMentionablePrompts('character2video', {
    onReferencePromptsChange: () => {
      // reference 变化时，如果目前在专业模式，且有权限使用，则提示
      if (isProMode && canUseProMode) {
        setChar2VideoWarningTipVisible(true);
      }
    },
    allowTempMode: true,
  });

  // 处理登录后的临时图片上传
  useRunTempReferenceImageUploadBatch(referencePrompts, updateReferenceAndMentions);
  // 处理登录后的临时视频上传
  useReferenceTempVideosUpload(referencePrompts, updateReferenceAndMentions);
  // 处理登录后的临时音频上传
  useReferenceTempAudiosUpload(referencePrompts, updateReferenceAndMentions);

  const resolveVoiceAudio = useCallback(
    async (audio: SourceAudio): Promise<SourceAudio | null> => {
      const duration = await resolveReferenceAudioDuration(audio);
      if (
        duration === undefined ||
        !isReferenceAudioDurationValid(
          duration,
          Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
          Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
        )
      ) {
        showReferenceAudioDurationError(duration);
        return null;
      }
      return { ...audio, duration };
    },
    [showReferenceAudioDurationError],
  );

  const getElementReferenceExtras = useCallback(
    async ({
      element,
      reference,
      referencePrompts,
    }: {
      element: ElementInfo;
      reference: Extract<ReferencePrompt, { type: 'element' }>;
      referencePrompts: ReferencePrompt[];
    }) => {
      if (modelVersion !== MODEL_VERSION.Q4 || !element.id) return undefined;

      const voiceAudio = getElementVoiceAudio(element);
      const canUseElementVoice = !!voiceAudio && getAudioReferenceCount(referencePrompts) < MAX_AUDIO_REFERENCE_PROMPTS;
      reference.useElementVoice = canUseElementVoice;

      if (!canUseElementVoice || !voiceAudio) return undefined;

      const validatedAudio = await resolveVoiceAudio(voiceAudio);
      if (!validatedAudio) {
        reference.useElementVoice = false;
        return undefined;
      }

      const audioReference: ReferencePrompt = {
        type: 'audio',
        id: getElementVoiceAudioId(element.id),
        name: validatedAudio.name || getNextAudioName(getNextAvailableAudioIdx(referencePrompts, locale), locale),
        status: 'done',
        prompt: validatedAudio,
      };

      return {
        extraReferences: [audioReference],
        appendItems: () => {
          const items = [
            {
              type: 'mention' as const,
              mention: {
                id: reference.id,
                name: reference.name,
                color: getMentionColor({ element: reference.prompt as ElementInfo, type: reference.type }),
              },
            },
            { type: 'text' as const, text: t('create.char2VideoForm.audioReferenceConnector') },
            {
              type: 'mention' as const,
              mention: {
                id: audioReference.id,
                name: audioReference.name,
                color: getMentionColor({ element: audioReference.prompt as ElementInfo, type: audioReference.type }),
                mediaType: 'audio' as const,
              },
            },
          ];
          return items;
        },
      };
    },
    [locale, modelVersion, resolveVoiceAudio, t],
  );

  const {
    elementsDialogState,
    setElementsDialogState,
    validationErrorMessage,
    toggleElementsSelector,
    handleOpenElementCreateDialog,
    handleOpenElementViewDialog,
    handleElementsSelect,
    handleSaveElement,
    calculateTotalImageCount,
    handleBeforeDeleteMention,
    handleShowMaxReferenceError,
    closeConfirmDialog,
  } = useElementsManager({
    taskType: 'character2video',
    modelVersion,
    referencePrompts,
    addReferenceAndMentions,
    getElementReferenceExtras,
    removeReferenceAndMentions,
    updateReferenceAndMentions,
    updateTextPrompt,
    resetMaxReferenceError,
    formValidationErrorMessage,
  });

  const handleUpdateReference = useCallback(
    (id: string, updates: Partial<ReferencePrompt>) => {
      const existingReference = referencePrompts.find((ref) => ref.id === id);
      updateReferenceAndMentions(id, updates);

      const nextVoiceAudio = isElementVoiceAudioPrompt(updates.prompt ?? null) ? (updates.prompt as SourceAudio) : null;
      if (
        existingReference?.type === 'audio' &&
        existingReference.prompt?.isElementVoiceReference &&
        existingReference.prompt.originElementId &&
        (updates.status !== 'done' ||
          !nextVoiceAudio ||
          nextVoiceAudio.originElementId !== existingReference.prompt.originElementId)
      ) {
        updateReferenceAndMentions(existingReference.prompt.originElementId, {
          useElementVoice: false,
        } as Partial<ReferencePrompt>);
      }
    },
    [referencePrompts, updateReferenceAndMentions],
  );

  const handleRemoveReference = useCallback(
    (id: string, isRecoverable = true) => {
      const existingReference = referencePrompts.find((ref) => ref.id === id);
      removeReferenceAndMentions(id, isRecoverable);

      if (existingReference?.type === 'element') {
        const voiceAudioReference = referencePrompts.find(
          (ref) => ref.type === 'audio' && ref.prompt?.isElementVoiceReference && ref.prompt.originElementId === id,
        );
        if (voiceAudioReference) {
          removeReferenceAndMentions(voiceAudioReference.id, false);
        }
      }

      if (
        existingReference?.type === 'audio' &&
        existingReference.prompt?.isElementVoiceReference &&
        existingReference.prompt.originElementId
      ) {
        updateReferenceAndMentions(existingReference.prompt.originElementId, {
          useElementVoice: false,
        } as Partial<ReferencePrompt>);
      }
    },
    [referencePrompts, removeReferenceAndMentions, updateReferenceAndMentions],
  );

  const doBackFill = useMemoizedFn(
    ({
      timestamp,
      imagePrompt,
      inspirationId,
      inspirationFrom,
    }: {
      timestamp: number;
      imagePrompt: UploadImageType;
      inspirationId?: string;
      inspirationFrom?: string;
    }) => {
      const newReference: ReferencePrompt = {
        type: 'image',
        id: `backfill-ref-${timestamp}`,
        name: getNextImageName(1, locale),
        status: 'done',
        prompt: imagePrompt,
      };

      addReferenceAndMentions(newReference);

      if (inspirationId) {
        setFormParams('character2video', {
          inspirationId,
          inspirationFrom,
          inspirationImageUrl: imagePrompt.sourceImage,
        });
      }
    },
  );

  useBackfillData({
    doBackFill,
  });

  const { useAlternativeLayout, updateSubmitCount } = useLayoutManager();
  const { videoLoadState, setVideoLoadState, handleVideoOpenChange } = useVideoState();

  const { maxImageSize, imageProcessing } = useResizeByResolution('character2video');

  const nonVideoMaxSelections = useMemo(() => {
    const currentCount = supportVideoReference
      ? getImageAndImageElementCount(referencePrompts) + getTextElementCount(referencePrompts)
      : getImageAndElementCount(referencePrompts);
    return Math.max(0, maxImageAndElementCount - currentCount);
  }, [supportVideoReference, referencePrompts, maxImageAndElementCount]);

  useEffect(() => {
    if (elementsDialogState.type === 'elementsSelector') {
      setElementsDialogState({ type: 'none' });
    }
  }, [modelVersion]);

  const [remainingSelections, maxReferenceCount] = useMemo(() => {
    if (elementsDialogState.type === 'elementsSelector') {
      const allowedModalities = elementsDialogState.allowedModalities;
      // 只选视频主体时用模型的原始上限（2），允许一次挑两个：主体的时长能在入列前探测出来，
      // handleElementsSelect 会按「1 个 5-8s 或 2 个 5s 内」校验并拒绝超额组合。
      // 上传视频没有这个待遇——时长要等上传/元数据读完才知道，所以仍然一次只放 1 个槽位。
      if (allowedModalities?.length === 1 && allowedModalities[0] === 'video') {
        const maxVideoCount = getReferenceVideoElementMaxCount(modelVersion);
        return [Math.max(0, maxVideoCount - getVideoAndVideoElementCount(referencePrompts)), maxVideoCount];
      }
    }

    return [nonVideoMaxSelections, maxImageAndElementCount];
  }, [elementsDialogState, nonVideoMaxSelections, modelVersion, referencePrompts]);

  // 做同款时批量查询主体信息
  useCheckMaterialElements({
    referencePrompts,
    updateReferenceAndMentions,
    updateTextPrompt,
  });

  const hasDoneAudioReference = useMemo(
    () =>
      modelVersion === MODEL_VERSION.Q4 &&
      referencePrompts.some((prompt) => prompt.type === 'audio' && prompt.status === 'done' && !!prompt.prompt?.src),
    [modelVersion, referencePrompts],
  );
  const ensureReferenceAudioConsent = useCreationConsent(userInfo?.id, {
    requireReferenceAudioConsent: hasDoneAudioReference,
  });

  const validateQ4ReferenceAudiosBeforeSubmit = useCallback(async () => {
    if (!isQ4Reference) return true;

    const result = await validateReferenceAudioDurations(
      referencePrompts,
      Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
      Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
    );
    if (!result.ok) {
      showReferenceAudioDurationError(result.reason === 'outOfRange' ? result.duration : undefined);
      return false;
    }

    for (const resolved of result.resolved) {
      const reference = referencePrompts.find((item) => item.id === resolved.referenceId);
      if (reference?.type === 'audio' && reference.prompt?.duration !== resolved.duration) {
        updateReferenceAndMentions(reference.id, {
          prompt: { ...reference.prompt, duration: resolved.duration },
        });
      }
    }
    return true;
  }, [isQ4Reference, referencePrompts, showReferenceAudioDurationError, updateReferenceAndMentions]);

  const examples = useModelTrialCases('character2video', modelVersion, locale);
  const tutorials = useMemo(
    () =>
      getChar2VTutorials(modelVersion).map((tutorial) => {
        const index = examples.findIndex((example) => example.caseKey === tutorial.caseKey);
        return { ...tutorial, exampleIndex: index >= 0 ? index : undefined };
      }),
    [modelVersion, examples],
  );
  const { isMobile } = useDevice();
  const welcomeTitleHighlight = isQ4Reference
    ? t('create.char2VideoForm.welcome.q4.titleHighlight')
    : t('create.char2VideoForm.welcome.titleHighlight');
  const welcomeDescription = isQ4Reference
    ? t('create.char2VideoForm.welcome.q4.description')
    : t('create.char2VideoForm.welcome.description');
  const promptPlaceholder = isQ4Reference
    ? t('create.char2VideoForm.input.placeholder.q4')
    : supportVideoReference
      ? t('create.char2VideoForm.input.placeholder.q2Pro')
      : undefined;
  const { selectAudio } = useAudioUpload({
    minDuration: Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
    maxDuration: Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
    maxSize: Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
    accept: Q4_REFERENCE_AUDIO_ACCEPT,
    allowTempMode: true,
    getMaxBatchFiles: () => Math.max(1, MAX_AUDIO_REFERENCE_PROMPTS - getAudioReferenceCount(referencePrompts)),
    onChange: handleUploadAudio,
  });

  const handleVoiceConfirm = useCallback(
    async (voice: TTSVoiceOption | VoiceSelectionCustomOption | null, targetId?: string) => {
      if (!voice) return;

      const audio = await resolveVoiceAudio({
        id: voice.id,
        name: voice.name,
        src: voice.url,
        preview: voice.url,
        originElementVoiceId: voice.id,
        originElementVoiceIsMy: 'is_my' in voice && voice.is_my,
        isElementVoiceReference: true,
      });
      if (!audio) return;

      // 解析时长期间仍可添加其他素材，使用最新表单分配数量和名称。
      const currentForm = storeApi.getState().character2videoFormParams;
      if (currentForm.modelVersion !== MODEL_VERSION.Q4) return;
      const currentReferences = currentForm.referencePrompts ?? [];
      if (targetId) {
        if (!currentReferences.some((reference) => reference.id === targetId && reference.type === 'audio')) return;
        handleUpdateReference(targetId, { status: 'done', prompt: audio, name: voice.name });
        return;
      }
      if (getAudioReferenceCount(currentReferences) >= MAX_AUDIO_REFERENCE_PROMPTS) {
        handleShowMaxReferenceError(MAX_AUDIO_REFERENCE_PROMPTS);
        return;
      }

      addReferenceAndMentions({
        type: 'audio',
        id: uuidv4(),
        name: voice.name,
        status: 'done',
        prompt: audio,
      });
    },
    [addReferenceAndMentions, handleShowMaxReferenceError, handleUpdateReference, resolveVoiceAudio, storeApi],
  );

  const replacementAudioTargetRef = useRef<Extract<ReferencePrompt, { type: 'audio' }> | null>(null);
  const { selectAudio: selectReplacementAudio } = useAudioUpload({
    minDuration: Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
    maxDuration: Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
    maxSize: Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
    accept: Q4_REFERENCE_AUDIO_ACCEPT,
    allowTempMode: true,
    maxBatchFiles: 1,
    onChange: (file) => {
      const target = replacementAudioTargetRef.current;
      if (!target) return;
      if (file.status === 'uploading') {
        // 保留原音频，上传失败或取消时无需恢复素材与主体音色绑定。
        updateReferenceAndMentions(target.id, { status: 'uploading' });
        return;
      }
      const currentReferences = storeApi.getState().character2videoFormParams.referencePrompts ?? [];
      if (currentReferences.some((reference) => reference.id === target.id)) {
        if (file.status === 'done' && file.file) {
          handleUpdateReference(target.id, {
            status: 'done',
            prompt: file.file,
            name: getNextAudioName(
              getNextAvailableAudioIdx(
                currentReferences.filter((reference) => reference.id !== target.id),
                locale,
              ),
              locale,
            ),
          });
        } else {
          updateReferenceAndMentions(target.id, { status: 'done', prompt: target.prompt, name: target.name });
        }
      }
      replacementAudioTargetRef.current = null;
    },
  });

  const handleUploadReplacementAudio = (targetId: string) => {
    const reference = storeApi
      .getState()
      .character2videoFormParams.referencePrompts?.find(
        (reference) => reference.type === 'audio' && reference.id === targetId,
      );
    if (reference?.type !== 'audio') return;
    replacementAudioTargetRef.current = reference;
    selectReplacementAudio();
  };

  const renderAudioSelectionTrigger = (trigger: ReactNode) => (
    <VoiceSelectionDialog
      open={isVoiceDialogOpen === true}
      onOpenChange={setIsVoiceDialogOpen}
      title={t('elements.elementDialog.sound')}
      value={null}
      allowNoVoice={false}
      voiceInfoEditFieldSuffix="q4-reference"
      onConfirm={(_voiceId, voice) => handleVoiceConfirm(voice)}
      onUploadAudio={selectAudio}
      trigger={trigger}
    />
  );

  const handleFillExample = (index: number) => {
    const example = examples[index];
    if (!example) return;
    setPromptFromExample(example.text, example.images, example.videos, example.audios);
    updateSettings(
      validateAndCorrectSettings({
        version: modelVersion,
        type: 'character2video',
        newSettings: { ...settings, ...example.settings },
        t,
      }),
    );

    isInspireMe.current = true;
    reportEvent('try_samples_click', {
      from_page: 'create/character2video',
      samples_index: index + 1,
      sample_id: example.id,
    });
  };

  // 获取 mention 的 prefix（React node），为 video 类型添加图标
  const handleGetMentionPrefix = useCallback(
    (mentionId: string, _label: string): ReactNode | null => {
      const referencePrompt = referencePrompts.find((p) => p.id === mentionId);
      if (referencePrompt?.type === 'video') {
        return <BaseLineReferenceVideo className="shrink-0 text-sm text-system-white" />;
      }
      return null;
    },
    [referencePrompts],
  );

  useEffect(() => {
    if (showMaxReferenceError) {
      handleShowMaxReferenceError(maxImageAndElementCount);
    } else {
      closeConfirmDialog();
    }
  }, [showMaxReferenceError]);

  const headerVideoElement = (
    <div className="relative">
      <video
        className="w-full rounded-8"
        muted
        autoPlay
        loop
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        onLoadStart={() => setVideoLoadState('loading')}
        onCanPlay={() => setVideoLoadState('success')}
        onError={() => setVideoLoadState('error')}
      >
        <source src={CHAR2VIDEO_TUTORIAL_VIDEO_URL} type="video/mp4" />
      </video>
      <div
        className={cn(
          '-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 hidden transform text-system-white opacity-0 transition-opacity delay-1000',
          videoLoadState !== 'success' && 'block opacity-30',
        )}
      >
        {videoLoadState === 'loading' && <Loading className="animate-spin text-5xl duration-2000" />}
        {videoLoadState === 'error' && <span className="text-xs">{t('create.char2VideoForm.video.error')}</span>}
      </div>
    </div>
  );

  const welcomeHeaderElement = (
    <div className="mt-3 mb-4 flex justify-between gap-2 px-2">
      <div className="flex min-w-[160px] flex-1 flex-col">
        <p
          className={cn(
            'font-semibold text-sm text-white sm:text-base',
            useAlternativeLayout && 'text-base',
            locale === 'zh' && 'hidden',
          )}
        >
          {locale !== 'zh' && t('create.char2VideoForm.welcome.title')}
        </p>
        {!useAlternativeLayout ? (
          <>
            <p className={cn('font-semibold text-lg text-white sm:text-base', locale === 'zh' && 'pt-5 sm:pt-0')}>
              {welcomeTitleHighlight}
            </p>
            {isQ4Reference && <p className="mt-2 text-system-white64 text-xs">{welcomeDescription}</p>}
          </>
        ) : (
          <>
            <div className="flex items-center">
              <p className="font-semibold text-2xl text-white lg:text-xl">{welcomeTitleHighlight}</p>
              <CustomTooltip
                content={headerVideoElement}
                variant="extend"
                contentClassName="w-[400px] sm:w-full"
                sideOffset={8}
                disableHoverableContent={false}
                align="start"
                side="bottom"
                onOpenChange={handleVideoOpenChange}
              >
                <Button
                  className="ml-3 flex h-6 w-10 items-center justify-center rounded-[100px] border border-transparent bg-system-white08 text-white hover:border-system-blue01 hover:bg-system-blue02"
                  size="original"
                >
                  <BaseLinePlay className="text-sm" />
                </Button>
              </CustomTooltip>
            </div>
            <p className="mt-2 text-system-white64 text-xs">{welcomeDescription}</p>
          </>
        )}

        <div className="mt-3 flex gap-2 sm:flex-col">
          <ImageUploader
            className="flex h-8 items-center gap-1 rounded-8 border border-system-white08 bg-system-white04 px-3 py-1.5 text-white hover:bg-system-black64 sm:h-10 sm:w-[120px] sm:justify-center sm:text-xs"
            maxCount={
              maxImageAndElementCount -
              referencePrompts.filter(
                (prompt) =>
                  prompt.type === 'image' || (prompt.type === 'element' && prompt.prompt?.modality !== 'video'),
              ).length
            }
            getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
            onChange={(file) => handleUploadImage(file)}
            allowTempMode={true}
            maxFileSize={isQ4Reference ? Q4_REFERENCE_IMAGE_MAX_FILE_SIZE : undefined}
          >
            <BaseLinePlus className="size-4" />
            {t('create.char2VideoForm.element.addImages')}
          </ImageUploader>
          <Button
            className="flex h-8 items-center gap-1 rounded-8 border border-system-white08 bg-system-white04 px-3 py-1.5 text-white hover:bg-system-black64 sm:h-10 sm:w-[120px] sm:justify-center"
            onClick={() => toggleElementsSelector(allowedModalities)}
          >
            <BaseLinePlus className="size-4" />
            {t('create.char2VideoForm.element.addElements')}
          </Button>
          {isQ4Reference &&
            renderAudioSelectionTrigger(
              <Button className="flex h-8 items-center gap-1 rounded-8 border border-system-white08 bg-system-white04 px-3 py-1.5 text-white hover:bg-system-black64 sm:h-10 sm:w-[120px] sm:justify-center sm:text-xs">
                <BaseLinePlus className="size-4" />
                {t('create.char2VideoForm.audio')}
              </Button>,
            )}
        </div>
      </div>

      {!useAlternativeLayout && (
        <CustomTooltip
          content={headerVideoElement}
          variant="extend"
          contentClassName="w-[400px] sm:w-full"
          sideOffset={8}
          disableHoverableContent={false}
          align="start"
          side="bottom"
          onOpenChange={handleVideoOpenChange}
        >
          <div
            className="h-[113px] w-[150px] bg-center bg-cover bg-no-repeat lg:w-[120px]"
            style={{ backgroundImage: `url(${HeaderImage.src})` }}
          />
        </CustomTooltip>
      )}
    </div>
  );

  const handleGetExtraItems = () => referencePrompts?.filter((it) => it.status !== 'forbidden');

  const { close: closeFrameShotDialog, isOpen: isFrameShotDialogOpen } = useGlobalModal('frameShotDialog');

  useEffect(() => {
    if (elementsDialogState.type === 'elementsSelector') {
      if (isFrameShotDialogOpen()) {
        closeFrameShotDialog();
      }
    }
  }, [elementsDialogState.type]);

  const handleChar2VideoMediaDrop = useMemoizedFn(
    (file: UploadFile | AudioUploadFile, type: 'image' | 'video' | 'audio') => {
      if (type === 'video') {
        handleUploadVideo(file as VideoUploadFile);
        return;
      }
      if (type === 'image') {
        handleUploadImage(file as ImageUploadFile);
        return;
      }
      if (type === 'audio') {
        handleUploadAudio(file as AudioUploadFile);
      }
    },
  );

  const char2VideoMediaDropProps = useMemo(() => {
    if (supportVideoReference) {
      const mediaDropArgs = getReferenceMediaDropArgs(modelVersion, referencePrompts);
      return {
        className: cn('relative flex min-h-[50vh] w-full flex-none flex-col gap-2 rounded-12 bg-[#1A1C20] p-4 text-sm'),
        acceptImage: true as const,
        acceptVideo: true as const,
        maxImageCount: mediaDropArgs.maxImageCount,
        maxVideoCount: mediaDropArgs.maxVideoCount,
        minVideoDuration: mediaDropArgs.minVideoDuration,
        maxVideoDuration: mediaDropArgs.maxVideoDuration,
        maxVideoSize: mediaDropArgs.maxVideoSize,
        disabled: mediaDropArgs.dropDisabled,
        'data-create-input-panel': 'true',
        getSelectedImage: (url: string) => resizeSourceImage({ url, maxImageSize }),
        captureCoverImage: true,
        uploadCoverImage: true,
        allowTempMode: true,
        enablePaste: true,
      };
    }
    const remaining = maxImageAndElementCount - getImageAndElementCount(referencePrompts);
    const audioRemaining = isQ4Reference ? MAX_AUDIO_REFERENCE_PROMPTS - getAudioReferenceCount(referencePrompts) : 0;
    return {
      className: cn(
        'relative flex min-h-[50vh] w-full flex-none flex-col gap-2 rounded-12 bg-[#1A1C20] p-4 text-sm',
        'pb-3',
      ),
      acceptImage: true as const,
      acceptVideo: false as const,
      acceptAudio: isQ4Reference,
      maxImageCount: Math.max(0, remaining),
      maxImageFileSize: isQ4Reference ? Q4_REFERENCE_IMAGE_MAX_FILE_SIZE : undefined,
      maxVideoCount: 0,
      maxAudioCount: Math.max(0, audioRemaining),
      minAudioDuration: Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
      maxAudioDuration: Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
      maxAudioSize: Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
      audioAccept: Q4_REFERENCE_AUDIO_ACCEPT,
      disabled: remaining <= 0 && audioRemaining <= 0,
      'data-create-input-panel': 'true',
      getSelectedImage: (url: string) => resizeSourceImage({ url, maxImageSize }),
      allowTempMode: true,
      enablePaste: true,
    };
  }, [supportVideoReference, isQ4Reference, referencePrompts, maxImageSize, maxImageAndElementCount, modelVersion]);

  return (
    <>
      <PromptLibraryPopover
        open={openPromptLibraryPopover}
        onOpenChange={setOpenPromptLibraryPopover}
        taskType="character2video"
        shouldUpgrade={!canUseProMode}
        onOpenPlanDialog={() => handleOpenPlanDialog('prompt_pro_mode')}
      >
        <MediaDropZone {...char2VideoMediaDropProps} onChange={handleChar2VideoMediaDrop}>
          {/* Header 或者 referencePrompts 展示区域 */}
          {supportVideoReference ? (
            <VideoImageReferencesGrid
              referencePrompts={referencePrompts}
              maxImageSize={maxImageSize}
              onUpdateReference={handleUpdateReference}
              onRemoveReference={(id) => {
                handleRemoveReference(id);
                setPendingDeleteIds((prev) => [...prev, id]);
              }}
              onUploadImage={handleUploadImage}
              onUploadVideo={handleUploadVideo}
              onToggleElementsSelector={toggleElementsSelector}
              onOpenElementCreateDialog={handleOpenElementCreateDialog}
              onOpenElementViewDialog={handleOpenElementViewDialog}
              replaceImage={handleReplaceImage}
              handleSwapReferences={handleSwapReferences}
              modelVersion={modelVersion}
              maxImageAndElementCount={maxImageAndElementCount}
              allowTempMode={true}
              ref={flyEndTargetRef}
            />
          ) : referencePrompts.length === 0 ? (
            welcomeHeaderElement
          ) : (
            <ReferencePromptsGrid
              referencePrompts={referencePrompts}
              maxImageSize={maxImageSize}
              onUpdateReference={handleUpdateReference}
              onRemoveReference={(id) => {
                handleRemoveReference(id);
                setPendingDeleteIds((prev) => [...prev, id]);
              }}
              onUploadImage={handleUploadImage}
              onUploadAudio={handleUploadAudio}
              renderAudioReplacementTrigger={
                isQ4Reference
                  ? (id, audio) => (
                      <VoiceSelectionDialog
                        key="replace"
                        open={isVoiceDialogOpen === id}
                        onOpenChange={(open) => setIsVoiceDialogOpen(open ? id : false)}
                        title={t('elements.elementDialog.sound')}
                        value={audio.originElementVoiceId || null}
                        allowNoVoice={false}
                        voiceInfoEditFieldSuffix={`q4-reference-${id}`}
                        onConfirm={(_voiceId, voice) => handleVoiceConfirm(voice, id)}
                        onUploadAudio={() => handleUploadReplacementAudio(id)}
                        trigger={
                          <button type="button" aria-label={t('videoEditor.replace')}>
                            <BaseLineReplace className="text-system-white text-xl" />
                          </button>
                        }
                      />
                    )
                  : undefined
              }
              audioSelectionTrigger={
                isQ4Reference
                  ? renderAudioSelectionTrigger(
                      <button
                        type="button"
                        className="flex w-full cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08"
                      >
                        <BaseLinePlus className="text-base text-system-white" />
                        <span className="text-system-white64 text-xs">{t('create.char2VideoForm.audio')}</span>
                      </button>,
                    )
                  : undefined
              }
              onToggleElementsSelector={toggleElementsSelector}
              onOpenElementCreateDialog={handleOpenElementCreateDialog}
              onOpenElementViewDialog={handleOpenElementViewDialog}
              replaceImage={handleReplaceImage}
              handleSwapReferences={handleSwapReferences}
              maxCount={maxImageAndElementCount}
              allowAudio={modelVersion === MODEL_VERSION.Q4}
              allowTempMode={true}
              ref={flyEndTargetRef}
            />
          )}

          {/* 文本输入框 */}
          <FormTextarea
            id="mentions-textarea"
            className="min-h-[140px] flex-[1_0_auto]"
            taskType="character2video"
            basicTextPrompt={textPrompt}
            onCloseProMode={(value) => {
              // 检查把所有 @ 添加到末尾
              const valueWithMention = appendUnMentionedReferences(value, referencePrompts);
              updateTextPrompt(valueWithMention);
            }}
            onClearPrompt={handleClearPrompts}
            onOpenPromptLibraryPopover={() => setOpenPromptLibraryPopover(true)}
            allowMention={true}
            isWarningTipVisible={isProModeWarningTipVisible}
            setIsWarningTipVisible={setChar2VideoWarningTipVisible}
            onBeforeDeleteMention={handleBeforeDeleteMention}
            onBeforeAddMention={handleBeforeAddMention}
            getExtraItems={handleGetExtraItems}
            onOpenPlanDialog={() => handleOpenPlanDialog('prompt_pro_mode')}
            hideProMode={modelVersion !== MODEL_VERSION['2.0'] && modelVersion !== MODEL_VERSION.Q1}
            allowedModalities={allowedModalities}
          >
            <MentionsTextarea
              className="h-full min-h-[140px]"
              fitContainer
              minHeight={140}
              textPrompt={textPrompt}
              updateTextPrompt={updateTextPrompt}
              onBeforeDeleteMention={handleBeforeDeleteMention}
              onBeforeAddMention={handleBeforeAddMention}
              onClear={handleClearPrompts}
              getExtraItems={handleGetExtraItems}
              getPrefix={handleGetMentionPrefix}
              placeholder={promptPlaceholder}
              maxLength={getPromptMaxLength(modelVersion)}
              allowedModalities={allowedModalities}
            />
          </FormTextarea>
          {!isProMode && (
            <div className="flex h-9 items-center gap-2">
              {!isEnterpriseDistribution && (
                <UserGuide
                  type="character2video"
                  tutorials={tutorials}
                  onClickExample={handleFillExample}
                  manualUrl={modelVersion === MODEL_VERSION.Q4 ? Q4_MANUAL_URL : undefined}
                />
              )}
              {!isEnterpriseDistribution && examples.length > 0 && <div className="h-6 w-[1px] bg-system-white08" />}
              {examples.length > 0 && (
                <ExpandableTrySamplesButton
                  content={<TryImageSamples examples={examples} onClickExample={handleFillExample} />}
                />
              )}
            </div>
          )}
        </MediaDropZone>
      </PromptLibraryPopover>

      <Settings
        className="mt-3"
        settings={settings}
        updateSettings={updateSettings}
        includedOptions={settingsConfig}
        type="character2video"
      />

      <div className="absolute bottom-0 left-0 mt-4 flex w-full justify-center px-10 [background:linear-gradient(180deg,rgba(2,11,19,0)_0%,#020B13_76.04%)] sm:px-0 lg:mt-3">
        <FormSubmitButton
          taskType="character2video"
          processing={imageProcessing}
          disabled={imageProcessing}
          disabledTip={validationErrorMessage}
          isInspireMe={isInspireMe.current}
          onTrialsUpdated={handleCheckSettings}
          onSubmitSuccess={() => {
            updateSubmitCount();
            return undefined;
          }}
          extra={!validationErrorMessage && isProMode && !canUseProMode && <UpgradeLabel />}
          onBeforeSubmit={async () => {
            if (shouldUpgradeForModel) {
              handleUpgradeClick();
              return false;
            }
            if (!(await validateQ4ReferenceAudiosBeforeSubmit())) {
              return false;
            }
            if (supportVideoReference) {
              return true; // Q2Video 模式下不检查最大参考数量
            }

            const totalImageCount = calculateTotalImageCount();

            if (totalImageCount > maxImageAndElementCount) {
              toast({
                type: 'warning',
                message: t('create.char2VideoForm.tooManyImagesWarning.content', {
                  count: maxImageAndElementCount,
                }),
              });
            }

            if (isProMode) {
              const canSubmitProMode = handleBeforeProModeSubmit();
              if (!canSubmitProMode) {
                return false;
              }
            }
            return ensureReferenceAudioConsent();
          }}
        >
          {t('create.createForm.submit')}
        </FormSubmitButton>
      </div>

      {/* 主体选择器 & 主体查看/编辑/创建弹窗 */}
      <ElementsDialog
        mode={isMobile ? 'sheet' : 'drawer'}
        taskType="character2video"
        dialogState={elementsDialogState}
        onDialogStateChange={setElementsDialogState}
        remainingSelections={remainingSelections}
        maxReferenceCount={maxReferenceCount}
        disabledIds={referencePrompts.filter((prompt) => prompt.type === 'element').map((prompt) => prompt.id)}
        onElementsSelect={handleElementsSelect}
        onElementsDelete={(ids) => {
          ids.forEach((id) => {
            handleRemoveReference(id, false);
            removeReferenceFromTrash(id);
          });
        }}
        onSubjectSave={handleSaveElement}
        pendingDeleteIds={pendingDeleteIds}
        setPendingDeleteIds={setPendingDeleteIds}
      />
    </>
  );
};

export default Char2VideoForm;
